import React, { useEffect, useMemo, useState } from 'react';
import { Table, Container, Button } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import PurchaseModal from '../components/PurchaseModal';
import UnifiedPaymentModal from '../components/UnifiedPaymentModal';
import StatusLight from './StatusLight';
import usePermissions from '../utils/usePermissions';
import PaginationControls from '../components/PaginationControls';
import usePagination from '../utils/usePagination';
import { formatCurrency, getCurrencyPrefixFromStorage, getNumberingSystemFromStorage } from '../utils/numberingSystem';
import '../styles/CustomerList.css';
import '../styles/RecentDocuments.css';
import InlineNotice from '../components/InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import ListSearchBar from '../components/ListSearchBar';
import useDebouncedValue from '../utils/useDebouncedValue';
import { getActiveSearchTerm, shouldTriggerSearchRequest } from '../utils/searchThreshold';

const PurchaseListPage = () => {
  const { can } = usePermissions('purchases');
  const { showInlineNotice } = useInlineNotice();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isPurchaseViewOpen, setIsPurchaseViewOpen] = useState(false);
  const [isPurchaseEditOpen, setIsPurchaseEditOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [purchaseToView, setPurchaseToView] = useState(null);
  const [purchaseToEdit, setPurchaseToEdit] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({ status: null });
  const [sortConfig, setSortConfig] = useState({
    column: 'date',
    direction: 'desc'
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const permissions = useMemo(() => ({
    canCreate: can('create'),
    canEdit: can('editDraft'),
    canCancel: can('cancel'),
    canDelete: can('delete'),
    canPayments: can('payments')
  }), [can]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [purchaseToDelete, setPurchaseToDelete] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [purchaseToCancel, setPurchaseToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const activeSearchTerm = getActiveSearchTerm(debouncedSearch);
  const shouldTriggerSearch = shouldTriggerSearchRequest(debouncedSearch);


  const fetchPurchases = async (searchTerm = '') => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/purchases`, {
        params: searchTerm ? { search: searchTerm } : undefined,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setPurchases(response.data || []);
    } catch (err) {
      console.error('Error fetching purchases:', err);
      setError('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldTriggerSearch) {
      return;
    }
    fetchPurchases(activeSearchTerm);
  }, [activeSearchTerm, shouldTriggerSearch]);

  const handlePurchaseSaved = () => {
    fetchPurchases(activeSearchTerm);
    setSelectedRows([]);
  };

  const handlePaymentClick = (purchase) => {
    if (!permissions.canPayments) {
      return;
    }
    if (purchase.status === 'Cancelled' || !['Confirmed', 'In Progress'].includes(purchase.status)) {
      return; // Don't open payment modal for cancelled purchases
    }
    setSelectedPurchase(purchase);
    setIsPaymentModalOpen(true);
  };

  const handleCheckboxChange = (purchaseId) => {
    setSelectedRows(prev =>
      prev.includes(purchaseId)
        ? [] // Deselect if already selected
        : [purchaseId] // Select only this row, deselecting any others
    );
  };

  const handleViewPurchase = () => {
    if (selectedRows.length === 1) {
      const purchase = filteredPurchases.find(p => p._id === selectedRows[0]);
      if (purchase) {
        setPurchaseToView(purchase);
        setIsPurchaseViewOpen(true);
      }
    }
  };

  const handleEditPurchase = () => {
    if (selectedRows.length === 1) {
      if (!permissions.canEdit) return;
      const purchase = filteredPurchases.find(p => p._id === selectedRows[0]);
      if (purchase && (purchase.status || 'Draft') === 'Draft') {
        setPurchaseToEdit(purchase);
        setIsPurchaseEditOpen(true);
      }
    }
  };

  const handlePaymentSaved = () => {
    fetchPurchases(activeSearchTerm);
    setSelectedRows([]);
    showInlineNotice('Payment saved successfully.', {
      scope: 'purchases'
    });
  };

  const handleDeletePurchase = (purchase) => {
    if (!permissions.canDelete) return;
    setPurchaseToDelete(purchase);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePurchase = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/purchases/${purchaseToDelete._id}`);
      const number = purchaseToDelete?.purchaseNumber || purchaseToDelete?.invoiceNumber || 'purchase';
      showInlineNotice(`Purchase ${number} deleted successfully.`, { scope: 'purchases' });
      await fetchPurchases(activeSearchTerm);
      setSelectedRows([]);
      setShowDeleteConfirm(false);
      setPurchaseToDelete(null);
    } catch (err) {
      console.error('Error deleting purchase:', err);
      setError(err.response?.data?.error || 'Failed to delete purchase');
      showInlineNotice('Failed to delete purchase.', {
        scope: 'purchases',
        type: 'error'
      });
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelDocument = () => {
    if (selectedRows.length !== 1) return;
    if (!permissions.canCancel) return;
    const docToCancel = filteredPurchases.find(doc => doc._id === selectedRows[0]);
    if (docToCancel && ['Draft', 'Confirmed'].includes(docToCancel.status || 'Draft')) {
      setPurchaseToCancel(docToCancel);
      setCancelReason('');
      setShowCancelConfirm(true);
    }
  };

  const confirmCancelDocument = async () => {
    try {
      if (!cancelReason.trim()) {
        setError('Cancellation reason is required.');
        return;
      }
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/purchases/${purchaseToCancel._id}/cancel`,
        { cancellationReason: cancelReason.trim() },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const number = purchaseToCancel?.purchaseNumber || purchaseToCancel?.invoiceNumber || 'purchase';
      showInlineNotice(`Purchase ${number} cancelled successfully.`, { scope: 'purchases' });
      
      await fetchPurchases(activeSearchTerm);
      setShowCancelConfirm(false);
      setPurchaseToCancel(null);
      setCancelReason('');
      setSelectedRows([]);
    } catch (err) {
      console.error('Error cancelling purchase:', err);
      setError(err.response?.data?.error || 'Failed to cancel purchase');
      showInlineNotice('Failed to cancel purchase.', {
        scope: 'purchases',
        type: 'error'
      });
      setShowCancelConfirm(false);
    }
  };

  const handleReload = async () => {
    setSelectedRows([]);
    await fetchPurchases(activeSearchTerm);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return formatCurrency(Number(amount) || 0, getNumberingSystemFromStorage(), {
      prefix: getCurrencyPrefixFromStorage()
    });
  };

  // Sorting logic
  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      column,
      direction: prevConfig.column === column && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedPurchases = (purchasesToSort) => {
    if (!sortConfig.column) return purchasesToSort;

    return [...purchasesToSort].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.column) {
        case 'vendorName':
          aValue = (a.vendorName || '').toLowerCase();
          bValue = (b.vendorName || '').toLowerCase();
          break;
        case 'invoiceNumber':
          aValue = (a.invoiceNumber || '').toLowerCase();
          bValue = (b.invoiceNumber || '').toLowerCase();
          break;
        case 'purchaseNumber':
          aValue = (a.purchaseNumber || '').toLowerCase();
          bValue = (b.purchaseNumber || '').toLowerCase();
          break;
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'total':
        case 'subTotal':
        case 'tax':
        case 'totalPaid':
          aValue = parseFloat(a[sortConfig.column]) || 0;
          bValue = parseFloat(b[sortConfig.column]) || 0;
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filtering logic
  const getFilteredPurchases = () => {
    let filtered = [...purchases];

    // Status filter
    if (selectedFilters.status) {
      filtered = filtered.filter(p => p.status === selectedFilters.status);
    }

    return filtered;
  };

  const filteredPurchases = getSortedPurchases(getFilteredPurchases());
  const {
    page,
    setPage,
    totalPages,
    pageItems: pagedPurchases,
    totalItems,
    pageSize
  } = usePagination(filteredPurchases, 10);

  useEffect(() => {
    setSelectedRows([]);
  }, [page, activeSearchTerm]);

  const handleFilterChange = (status) => {
    setSelectedFilters({ status });
  };

  const handleClearFilters = () => {
    setSelectedFilters({ status: null });
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="alert alert-danger">{error}</div>
      </Container>
    );
  }

  return (
    <div className="recent-invoices">
      <div className="recent-invoices-header">
        <h1>Purchases <span className="text-muted">({filteredPurchases.length})</span></h1>
      </div>
      {error && <div className="recent-error">{error}</div>}
      <InlineNotice scope="purchases" />
      <div className="recent-invoices-list">
        <div className="recent-invoices-table-header">
          <div className="recent-invoices-actions" style={{ justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleReload}
            disabled={loading}
            className="recent-invoices-btn recent-invoices-btn-reload"
          >
            {loading ? (
              <>
                <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/><path d="M11 1a9 9 0 0 1 9 9"/></svg>
                Loading...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                Reload
              </>
            )}
          </button>
          <button
            className="recent-invoices-btn recent-invoices-btn-view"
            onClick={handleViewPurchase}
            disabled={selectedRows.length === 0}
            title="View purchase details"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            View
          </button>
          <button
            className="recent-invoices-btn recent-invoices-btn-copy"
            onClick={handleEditPurchase}
            disabled={
              !permissions.canEdit ||
              selectedRows.length !== 1 ||
              (filteredPurchases.find(p => p._id === selectedRows[0])?.status || 'Draft') !== 'Draft'
            }
            title="Edit selected draft purchase"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
            Edit
          </button>
          <button 
            className="recent-invoices-btn recent-invoices-btn-filter"
            onClick={() => setShowFilterModal(true)}
            title="Filter purchases by status"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            Filter
          </button>
          {permissions.canCancel && (
            <button 
              onClick={() => {
                if (selectedRows.length === 1) {
                  const purchase = filteredPurchases.find(p => p._id === selectedRows[0]);
                  if (purchase && ['Draft', 'Confirmed'].includes(purchase.status || 'Draft')) {
                    setPurchaseToCancel(purchase);
                    setShowCancelConfirm(true);
                  }
                }
              }}
              disabled={
                selectedRows.length !== 1 ||
                !['Draft', 'Confirmed'].includes(filteredPurchases.find(p => p._id === selectedRows[0])?.status || 'Draft')
              }
              className="recent-invoices-btn recent-invoices-btn-cancel"
              title="Cancel selected purchase"
              style={{ backgroundColor: '#f97316', color: 'white' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              Cancel
            </button>
          )}
          {permissions.canDelete && (
            <button
              onClick={() => {
                if (selectedRows.length === 1) {
                  handleDeletePurchase(filteredPurchases.find(p => p._id === selectedRows[0]));
                }
              }}
              disabled={selectedRows.length === 0}
              className="recent-invoices-btn recent-invoices-btn-delete"
              title="Delete selected purchase"
              style={{ backgroundColor: '#ef4444', color: 'white' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
              Delete
            </button>
          )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ListSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by Purchase #, Vendor"
                disabled={loading}
                isSearching={loading && Boolean(activeSearchTerm)}
              />
              {permissions.canCreate && (
                <button
                  onClick={() => setIsPurchaseModalOpen(true)}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add
                </button>
              )}
            </div>
          </div>
        </div>
        {loading ? (
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredPurchases.length === 0 ? (
          <div className="recent-invoices-empty">
            {selectedFilters.status
              ? 'No purchases match the selected filter'
              : activeSearchTerm
                ? 'No purchases match your search'
                : 'No purchases yet'}
            {selectedFilters.status && (
              <button 
                onClick={() => setSelectedFilters({ status: null })}
                style={{
                  marginTop: '10px',
                  padding: '6px 12px',
                  backgroundColor: '#0ea5e9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <table className="recent-invoices-table">
            <thead>
              <tr>
                <th></th>
                <th onClick={() => handleSort('purchaseNumber')} className={`sortable ${sortConfig.column === 'purchaseNumber' ? 'sorted' : ''}`}>
                  Purchase # {sortConfig.column === 'purchaseNumber' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('vendorName')} className={`sortable ${sortConfig.column === 'vendorName' ? 'sorted' : ''}`}>
                  Vendor {sortConfig.column === 'vendorName' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('invoiceNumber')} className={`sortable ${sortConfig.column === 'invoiceNumber' ? 'sorted' : ''}`}>
                  Vendor Invoice # {sortConfig.column === 'invoiceNumber' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('date')} className={`sortable ${sortConfig.column === 'date' ? 'sorted' : ''}`}>
                  Date {sortConfig.column === 'date' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('total')} className={`sortable ${sortConfig.column === 'total' ? 'sorted' : ''}`}>
                  Total {sortConfig.column === 'total' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('totalPaid')} className={`sortable ${sortConfig.column === 'totalPaid' ? 'sorted' : ''}`}>
                  Total Paid {sortConfig.column === 'totalPaid' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
                <th onClick={() => handleSort('status')} className={`sortable ${sortConfig.column === 'status' ? 'sorted' : ''}`}>
                  Status {sortConfig.column === 'status' && <span className={`sort-indicator ${sortConfig.direction}`}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>}
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedPurchases.map((purchase) => {
                const totalPaid = purchase.totalPaid || 0;
                const remaining = purchase.total - totalPaid;
                const isCompleted = purchase.status === 'Completed';

                return (
                  <tr
                    key={purchase._id}
                    className={selectedRows.includes(purchase._id) ? 'selected' : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(purchase._id)}
                        onChange={() => handleCheckboxChange(purchase._id)}
                      />
                    </td>
                    <td>{purchase.purchaseNumber || '-'}</td>
                    <td>
                      <div className="fw-semibold">{purchase.vendorName}</div>
                      {purchase.gst && (
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          GST: {purchase.gst}
                        </div>
                      )}
                    </td>
                    <td>{purchase.invoiceNumber || '-'}</td>
                    <td>{formatDate(purchase.date)}</td>
                    <td>
                      <div>{formatAmount(purchase.total)}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        Tax: {formatAmount(purchase.tax)}
                      </div>
                    </td>
                    <td>
                      <div>{formatAmount(totalPaid)}</div>
                      {!isCompleted && (
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                          Remaining: {formatAmount(remaining)}
                        </div>
                      )}
                    </td>
                    <td>
                      <StatusLight status={purchase.status} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </div>

      {/* Modals */}
      <PurchaseModal
        isOpen={isPurchaseModalOpen}
        onClose={() => setIsPurchaseModalOpen(false)}
        onPurchaseSaved={handlePurchaseSaved}
      />

      <PurchaseModal
        isOpen={isPurchaseViewOpen}
        onClose={() => {
          setIsPurchaseViewOpen(false);
          setPurchaseToView(null);
        }}
        purchase={purchaseToView}
        onPurchaseSaved={handlePurchaseSaved}
      />

      <PurchaseModal
        isOpen={isPurchaseEditOpen}
        onClose={() => {
          setIsPurchaseEditOpen(false);
          setPurchaseToEdit(null);
        }}
        purchase={purchaseToEdit}
        isEditable
        onPurchaseSaved={handlePurchaseSaved}
      />

      <UnifiedPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setSelectedPurchase(null);
        }}
        paymentType="purchase"
        entity={selectedPurchase}
        onPaymentSaved={handlePaymentSaved}
      />

      {/* Filter Modal */}
      {showFilterModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowFilterModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              Filter Purchases
            </h2>

            {/* Status Filter */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '10px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Status
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {['Draft', 'Confirmed', 'In Progress', 'Completed', 'Cancelled'].map(status => (
                  <button
                    key={status}
                    onClick={() => handleFilterChange(selectedFilters.status === status ? null : status)}
                    style={{
                      padding: '10px',
                      backgroundColor: selectedFilters.status === status ? '#0ea5e9' : '#f3f4f6',
                      border: selectedFilters.status === status ? '2px solid #0284c7' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      color: selectedFilters.status === status ? 'white' : '#374151',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear and Close Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleClearFilters}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: '#1f2937',
                  transition: 'all 0.2s ease'
                }}
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#0ea5e9',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: 'white',
                  transition: 'all 0.2s ease'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && purchaseToCancel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowCancelConfirm(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
              Cancel Purchase
            </h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              Are you sure you want to cancel this purchase from <strong>{purchaseToCancel.vendorName}</strong>? This will mark the purchase as cancelled.
            </p>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Cancellation Reason
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '16px',
                resize: 'vertical'
              }}
              placeholder="Enter reason for cancellation"
            />
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowCancelConfirm(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: '#1f2937'
                }}
              >
                No, Keep It
              </button>
              <button
                onClick={confirmCancelDocument}
                disabled={!cancelReason.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f97316',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: cancelReason.trim() ? 'pointer' : 'not-allowed',
                  color: 'white'
                }}
              >
                Yes, Cancel Purchase
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && purchaseToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowDeleteConfirm(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
              Delete Purchase
            </h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
              Are you sure you want to delete this purchase from <strong>{purchaseToDelete.vendorName}</strong> for <strong>{formatAmount(purchaseToDelete.total)}</strong>? This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePurchase}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseListPage;
